import { ErrorMessage, Formik } from "formik";
import { FC, useContext, useState } from "react";
import Select from "react-select"; // Added react-select
import * as yup from "yup";

import appStyles from "../../../App.module.css";
import appConstants, { convertDateToApiFormat } from "../../../app-constants";
import { AUBusinessTravelContext } from "../../AUBusinessTravel";
import { businessTravelDocs, colourStyles } from "../../config/bt-constants"; // Imported colourStyles
import { IAUBusinessTravelContext, IOrderFormValues, IinitialQuote } from "../../config/interfaces";
import Popup from "../Popup";
import QuoteApi from "../api/QuoteApi";
import Footer from "../common/Footer";
import QuoteSummary from "../common/QuoteSummary";
import YourQuote from "../common/YourQuote";
import LoadingModal from "../modal/LoadingModal";

interface OrderFormProps { }

// Extended interface to include 'state' without breaking your external file
interface IOrderFormValuesExtended extends IOrderFormValues {
  state_code: string;
  abn: string;
}

const OrderForm: FC<OrderFormProps> = (props: OrderFormProps) => {
  const { quoteData, setQuoteData, next, setOrder, setLoader, loader, tripsData } = useContext(
    AUBusinessTravelContext
  ) as IAUBusinessTravelContext;

  const [hasError, setHasError] = useState<string | null>(null);

  // Extract AU States from the API payload
  const auCountry = tripsData?.hostCountries?.find((c: any) => c.code === "AU" || c.code === "AUS");
  const auStates = auCountry?.states || [];

  const initialFormValues: IOrderFormValuesExtended = {
    firstName: "",
    lastName: "",
    jobTitle: "",
    email: "",
    phone: "",
    companyName: "",
    abn: '',
    companyAddress: "",
    postcode: "",
    city: "",
    state_code: "", // Added state
    host_country: "AU", // Hardcoded to AU 
    industry: "",
  };

  const validationSchema = yup.object().shape({
    firstName: yup.string().trim().required(),
    lastName: yup.string().trim().required(),
    jobTitle: yup.string().trim().required(),
    email: yup
      .string()
      .email("Invalid email address")
      .trim()
      .matches(appConstants.EMAIL_REGEX, "Invalid email address")
      .required(),
    phone: yup.string().trim().required(),
    companyName: yup.string().trim().required(),
    abn: yup.string()
      .required('ABN is required')
      .matches(/^\d{11}$/, 'ABN must be exactly 11 digits'),
    companyAddress: yup.string().trim().required(),
    postcode: yup.string().trim().required(),
    city: yup.string().trim().required(),
    state_code: yup.string().required("This field is required"), // Added state validation
    industry: yup.string().trim().required(),
  });

  const handleFormSubmit = async (values: IOrderFormValuesExtended) => {
    try {
      setLoader("order");
      const _quoteData: IinitialQuote = { ...quoteData };      

      const _applicant = {
        first_name: values.firstName,
        last_name: values.lastName,
        email: values.email,
        contact_number: values.phone,
        address: values.companyAddress,
        post_code: values.postcode,
        city: values.city,
        state_code: values.state_code, // Map state code to payload
        host_country: "AU",  // Hardcode AU
        country_code: "AU",  // Hardcode AU
      };

      const _applicants = [{ ...quoteData.applicants[0], ..._applicant }];

      _quoteData.applicants = _applicants;
      setQuoteData(_quoteData);
      const _depositDate = new Date();
      _depositDate.setDate(_depositDate.getDate() - 1);

      const _createOrderApiData = {
        currency_id: quoteData?.currency,
        product_id: quoteData?.productId,
        start_at: convertDateToApiFormat(quoteData.start_date),
        end_at: convertDateToApiFormat(quoteData.end_date),
        deposit_at: convertDateToApiFormat(_depositDate),
        details: [
          {
            type: "package",
            product_package_id: "base_package",
          },
          {
            "detail": "level",
            "product_package_id": "base_package",
            "type": "level-condition",
            "value": (quoteData.packageLevel || 1).toString(),
          }
        ],
        applicants: _applicants,
        meta: {
          company_name: values.companyName,
          job_title: values.jobTitle,
          industry: values.industry,
          company_size: quoteData.noOfEmployee, // This is not being captured in the form, so using the existing value from quoteData
          abn: values.abn,
        },
        destinations: [
          {
            destination_group: tripsData?.destinationCountries?.name,
          },
          {
            country_code: tripsData?.host_country?.code || "AU",
          }
        ],
        affiliate: {
          id: quoteData?.affiliateId,
        },
      };
      const createOrderResponse = await QuoteApi.createOrder(_createOrderApiData);

      if (createOrderResponse.errors) {
        throw createOrderResponse;
      }

      setOrder(createOrderResponse.data);
      next();
    } catch (e: any) {
      setHasError(e.message);
      console.log(e);
    } finally {
      setLoader(null);
    }
  };

  return (
    <>
      {loader && (
        <Popup onClose={() => { }} title="" children={<LoadingModal />} closeButton={false} LoadingModal={true} />
      )}
      <div className={`${appStyles.mainContainer} ${appStyles.btMainContainer}`}>
        <div className={`${appStyles.quoteResultWrapper} ${appStyles.btQuoteResultWrapper}`}>
          <div className={`${appStyles.quoteDetailsBox}`}>
            <YourQuote />

            <Formik initialValues={initialFormValues} validationSchema={validationSchema} onSubmit={handleFormSubmit}>
              {({
                values,
                errors,
                touched,
                handleBlur,
                isValid,
                dirty,
                handleSubmit,
                handleChange,
                setFieldValue,
                validateForm,
              }) => (
                <form
                  onSubmit={async (e) => {
                    e.preventDefault();
                    await validateForm();
                    handleSubmit();
                  }}
                >
                  <div className={appStyles.btDestinationsInfoWrap}>
                    <p className={appStyles.btDestinationsInfoHeading}>Maximum Trip duration 31 days</p>
                    <b>
                      This policy covers trips on a worldwide basis except for travel to a country or region issued with a “Level 4 - Do not travel” advice by the Department of Foreign Affairs and Trade (DFAT) on <a
                        href="https://www.smartraveller.gov.au"
                        className={appStyles.btFooterLink}
                        target="_blank"
                        rel="noopener noreferrer"
                      > smartraveller.gov.au </a>.
                    </b>
                  </div>
                  <div className={appStyles.formRow}>
                    <div className={appStyles.secondaryText}>
                      <span className={appStyles.infoIcon}></span>
                      <p>{businessTravelDocs?.warningMessage}</p>
                    </div>
                  </div>

                  <div
                    className={`${appStyles.quoteFormBlock} ${appStyles.quoteFormPersonalInformation} ${appStyles.businessBorderBox}`}
                  >
                    <div className={`${appStyles.quoteFormBlockTitle} ${appStyles.policyInfoWrap}`}>
                      <h3 className={`${appStyles.btTitle}`}>Your info</h3>
                      <p className={appStyles.btSubTitle}>
                        Please provide details of the primary contact for this insurance policy
                      </p>
                    </div>

                    <div className={`${appStyles.formRow} ${appStyles.btOrderFormRow}`}>
                      <div className={appStyles.formBlock}>
                        <div
                          className={`${appStyles.formField} ${touched?.firstName && errors?.firstName ? appStyles.hasError : ""
                            }`}
                        >
                          <label className={appStyles.btFormLabel}>First name</label>
                          <input
                            type={"text"}
                            name={"firstName"}
                            id={"firstName"}
                            value={values.firstName}
                            className={`${appStyles.bfFormControl} ${appStyles.bfFormControlSimple} ${appStyles.btInput} ${appStyles.btPlaceholder}`}
                            placeholder={"First name"}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                          <ErrorMessage name={"firstName"}>
                            {(msg) => <div className={appStyles.formError}>This field is required</div>}
                          </ErrorMessage>
                        </div>
                      </div>

                      <div className={appStyles.formBlock}>
                        <div
                          className={`${appStyles.formField} ${touched?.lastName && errors?.lastName ? appStyles.hasError : ""
                            }`}
                        >
                          <label className={appStyles.btFormLabel}>Last name</label>
                          <input
                            type={"text"}
                            name={"lastName"}
                            id={"lastName"}
                            value={values.lastName}
                            className={`${appStyles.bfFormControl} ${appStyles.bfFormControlSimple} ${appStyles.btInput} ${appStyles.btPlaceholder}`}
                            placeholder={"Last name"}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                          <ErrorMessage name={"lastName"}>
                            {(msg) => <div className={appStyles.formError}>This field is required</div>}
                          </ErrorMessage>
                        </div>
                      </div>
                    </div>

                    <div className={appStyles.formRow}>
                      <div className={appStyles.formBlock}>
                        <div className={appStyles.formRow}>
                          <label className={appStyles.btFormLabel}>Job title</label>
                        </div>
                        <div
                          className={`${appStyles.formField} ${touched?.jobTitle && errors?.jobTitle ? appStyles.hasError : ""
                            }`}
                        >
                          <input
                            type={"text"}
                            name={"jobTitle"}
                            id={"jobTitle"}
                            value={values.jobTitle}
                            className={`${appStyles.bfFormControl} ${appStyles.bfFormControlSimple} ${appStyles.btInput} ${appStyles.btPlaceholder}`}
                            placeholder={"Enter your job title"}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                          <ErrorMessage name={"jobTitle"}>
                            {(msg) => <div className={appStyles.formError}>This field is required</div>}
                          </ErrorMessage>
                        </div>
                      </div>
                    </div>

                    {/* Email is now 100% width since Phone was moved */}
                    <div className={appStyles.formRow}>
                      <div className={appStyles.formBlock}>
                        <div
                          className={`${appStyles.formField} ${touched?.email && errors?.email ? appStyles.hasError : ""
                            }`}
                        >
                          <label className={appStyles.btFormLabel}>Email</label>
                          <input
                            type={"text"}
                            name={"email"}
                            id={"email"}
                            value={values?.email}
                            className={`${appStyles.bfFormControl} ${appStyles.bfFormControlSimple} ${appStyles.btInput} ${appStyles.btPlaceholder}`}
                            placeholder={"Email address"}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                          <ErrorMessage name={"email"}>
                            {(msg) => <div className={appStyles.formError}>This field is required</div>}
                          </ErrorMessage>
                        </div>
                      </div>
                    </div>

                    {/* Company Info  */}
                    <div>
                      <div className={appStyles.quoteFormBlockTitle}>
                        <h3 className={appStyles.btTitle}>Company info</h3>
                      </div>

                      <div className={appStyles.formRow}>
                        <label className={appStyles.btFormLabel}>Company name</label>
                      </div>
                      <div className={appStyles.formRow}>
                        <div className={appStyles.formBlock}>
                          <div
                            className={`${appStyles.formField} ${touched?.companyName && errors?.companyName ? appStyles.hasError : ""
                              }`}
                          >
                            <input
                              type={"text"}
                              name={"companyName"}
                              id={"companyName"}
                              value={values.companyName}
                              className={`${appStyles.bfFormControl} ${appStyles.bfFormControlSimple} ${appStyles.btInput} ${appStyles.btPlaceholder}`}
                              placeholder={"Company name"}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            <ErrorMessage name={`companyName`}>
                              {(msg) => <div className={appStyles.formError}>This field is required</div>}
                            </ErrorMessage>
                          </div>
                        </div>
                      </div>

                      <div className={appStyles.formRow}>
                        <label className={appStyles.btFormLabel}>ABN</label>
                      </div>
                      <div className={appStyles.formRow}>  
                        <div className={appStyles.formBlock}>
                          <div className={`${appStyles.formField} ${touched?.abn && errors?.abn ? appStyles.hasError : ""}`}>
                            <input
                              type={"text"}
                              name={"abn"}
                              id={"abn"}
                              maxLength={11}
                              value={values.abn}
                              className={`${appStyles.bfFormControl} ${appStyles.bfFormControlSimple} ${appStyles.btInput} ${appStyles.btPlaceholder}`}
                              placeholder={"Enter 11 digit ABN"}
                              onBlur={handleBlur}
                              onChange={(e) => {
                                const onlyNumbers = e.target.value.replace(/\D/g, '');
                                setFieldValue("abn", onlyNumbers);
                              }}
                            />
                            <ErrorMessage name={"abn"}>
                              {(msg) => <div className={appStyles.formError}>{msg}</div>}
                            </ErrorMessage>
                          </div>
                        </div>
                      </div>

                      <div className={appStyles.formRow}>
                        <div className={appStyles.formBlock}>
                          <div
                            className={`${appStyles.formField} ${touched?.industry && errors?.industry ? appStyles.hasError : ""
                              }`}
                          >
                            <label className={appStyles.btFormLabel}>Industry</label>
                            <input
                              type={"text"}
                              name={"industry"}
                              id={"industry"}
                              value={values?.industry}
                              className={`${appStyles.bfFormControl} ${appStyles.bfFormControlSimple} ${appStyles.btInput} ${appStyles.btPlaceholder}`}
                              placeholder={"Enter the industry you work in"}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            <ErrorMessage name={"industry"}>
                              {(msg) => <div className={appStyles.formError}>This field is required</div>}
                            </ErrorMessage>
                          </div>
                        </div>
                      </div>

                      {/* Phone Number (Moved here, 50% width via flex layout trick) */}
                      <div style={{ display: "flex", gap: "15px" }} className={appStyles.formRow}>
                        <div className={appStyles.formBlock} style={{ flex: "0 0 calc(50% - 7.5px)" }}>
                          <div
                            className={`${appStyles.formField} ${touched?.phone && errors?.phone ? appStyles.hasError : ""
                              }`}
                          >
                            <label className={appStyles.btFormLabel}>Phone number</label>
                            <input
                              type={"text"}
                              name={"phone"}
                              id={"phone"}
                              value={values?.phone}
                              className={`${appStyles.bfFormControl} ${appStyles.bfFormControlSimple} ${appStyles.btInput} ${appStyles.btPlaceholder}`}
                              placeholder={"Phone number"}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            <ErrorMessage name={"phone"}>
                              {(msg) => <div className={appStyles.formError}>This field is required</div>}
                            </ErrorMessage>
                          </div>
                        </div>
                        {/* Empty block to push it to exactly 50% */}
                        <div className={appStyles.formBlock} style={{ flex: "0 0 calc(50% - 7.5px)" }}></div>
                      </div>

                      {/* Street Address (100%) */}
                      <div className={appStyles.formRow}>
                        <div className={appStyles.formBlock}>
                          <div className={appStyles.formRow}>
                            <label className={appStyles.btFormLabel}>Street address</label>
                          </div>
                          <div
                            className={`${appStyles.formField} ${touched?.companyAddress && errors?.companyAddress ? appStyles.hasError : ""
                              }`}
                          >
                            <input
                              type={"text"}
                              name={"companyAddress"}
                              id={"companyAddress"}
                              value={values.companyAddress}
                              className={`${appStyles.bfFormControl} ${appStyles.bfFormControlSimple} ${appStyles.btInput} ${appStyles.btPlaceholder}`}
                              placeholder={"Your business street address"}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            <ErrorMessage name={"companyAddress"}>
                              {(msg) => <div className={appStyles.formError}>This field is required</div>}
                            </ErrorMessage>
                          </div>
                        </div>
                      </div>

                      {/* City (66%) and Postcode (34%) side-by-side */}
                      <div style={{ display: "flex", gap: "15px" }} className={appStyles.formRow}>
                        <div className={appStyles.formBlock} style={{ flex: "0 0 calc(66.66% - 7.5px)" }}>
                          <div
                            className={`${appStyles.formField} ${touched?.city && errors?.city ? appStyles.hasError : ""
                              }`}
                          >
                            <label className={appStyles.btFormLabel}>City</label>
                            <input
                              type={"text"}
                              name={"city"}
                              id={"city"}
                              value={values.city}
                              className={`${appStyles.bfFormControl} ${appStyles.bfFormControlSimple} ${appStyles.btInput} ${appStyles.btPlaceholder}`}
                              placeholder={"City"}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            <ErrorMessage name={"city"}>
                              {(msg) => <div className={appStyles.formError}>This field is required</div>}
                            </ErrorMessage>
                          </div>
                        </div>

                        <div className={appStyles.formBlock} style={{ flex: "0 0 calc(33.33% - 7.5px)" }}>
                          <div
                            className={`${appStyles.formField} ${touched?.postcode && errors?.postcode ? appStyles.hasError : ""
                              }`}
                          >
                            <label className={appStyles.btFormLabel}>Post code</label>
                            <input
                              type={"text"}
                              name={"postcode"}
                              id={"postcode"}
                              value={values.postcode}
                              className={`${appStyles.bfFormControl} ${appStyles.bfFormControlSimple} ${appStyles.btInput} ${appStyles.btPlaceholder}`}
                              placeholder={"Post code"}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            <ErrorMessage name={"postcode"}>
                              {(msg) => <div className={appStyles.formError}>This field is required</div>}
                            </ErrorMessage>
                          </div>
                        </div>
                      </div>

                      {/* State Dropdown (100%) */}
                      <div className={appStyles.formRow}>
                        <div className={appStyles.formBlock}>
                          <div
                            className={`${appStyles.formField} ${touched?.state_code && errors?.state_code ? appStyles.hasError : ""
                              }`}
                          >
                            <label className={appStyles.btFormLabel}>State</label>
                            <Select
                              name="state_code"
                              id="state_code"
                              styles={colourStyles}
                              options={auStates}
                              getOptionLabel={(option: any) => option.name}
                              getOptionValue={(option: any) => option.code}
                              value={auStates.find((s: any) => s.code === values.state_code) || null}
                              onChange={(selectedOption: any) => {
                                setFieldValue("state_code", selectedOption ? selectedOption.code : "");
                              }}
                              onBlur={() => handleBlur({ target: { name: "state_code" } })}
                              placeholder={<div style={{ color: "#A8A8A8" }}>Select state</div>}
                              classNamePrefix="btSelect"
                              isClearable={true}
                              isSearchable={true}
                            />
                            <ErrorMessage name={"state_code"}>
                              {(msg) => <div className={appStyles.formError}>{msg}</div>}
                            </ErrorMessage>
                          </div>
                        </div>
                      </div>

                      {/* Country Row removed as requested */}
                    </div>
                  </div>

                  <div className={appStyles.formRow}>
                    <div className={`${appStyles.buttonBlock} `}>
                      <button
                        className={`${appStyles.bfButton}  ${appStyles.btButton100}`}
                        type={"submit"}
                        disabled={!isValid || !dirty}
                      >
                        PROCEED TO CHECKOUT
                      </button>
                    </div>
                  </div>
                  <Footer />
                </form>
              )}
            </Formik>
            {hasError && (
              <div className={appStyles.formRow}>
                <div className={appStyles.battlefaceServerError}>{hasError}</div>
              </div>
            )}
          </div>
          <QuoteSummary />
        </div>
      </div>
    </>
  );
};

export default OrderForm;